import {
  ArrowDownToLine,
  ArrowUpDown,
  ArrowUpToLine,
  ChevronUp,
  Star,
  X,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { CardFace } from "../cards/CardFace"
import { cardCssVars, FACTION_PALETTE } from "../cards/cardTheme"
import {
  AllyIcon,
  AttackIcon,
  DoubleAllyIcon,
  EnergyIcon,
  ExhaustIcon,
  SKILL_ICON,
} from "../cards/SkillIcon"
import { getSkillTooltip } from "../cards/skillText"
import { useIdbState } from "../hooks/useIdbState"
import { getSkillName } from "../i18n/gameContent"
import { useI18n } from "../i18n/useI18n"
import {
  ARTWORK_ASSET_STORAGE_KEY,
  ARTWORK_ASSET_STORAGE_VERSION,
  ArtworkAssetStoreSchema,
  EMPTY_ARTWORK_ASSET_STORE,
} from "../media/assets"
import { resolveArtworkUrl } from "../media/remote"
import type {
  ActionCommand,
  AuthoredCard,
  CardArtwork,
  CardColor,
  CardInstanceState,
  HeroDefinition,
  MatchSeatConnectionState,
  MatchState,
  SeatId,
  SeatState,
  SkillSelection,
} from "../schemas"
import { HERO_LEVEL_UP_COSTS } from "../schemas"
import { SKILL_MAP } from "./engine/content/skills"
import { executeCommand } from "./engine/match/execute"
import { getSeat, seatHasGuard } from "./engine/match/state"
import { getMatchCommandErrorLabel } from "./errorMessages"
import type { WsStatus } from "./useMatchWebSocket"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACTIVATABLE_SKILLS = new Set([
  "repair",
  "rally",
  "jam",
  "shift",
  "scout",
  "broker",
  "recycle",
  "salvage",
  "recover",
  "shield",
  "scrap",
])

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Props = {
  initialState: MatchState
  onReset: () => void
  cardDefs: Map<string, AuthoredCard>
  heroDefs: Map<string, HeroDefinition>
  controlledState?: MatchState
  onSendCommand?: (cmd: ActionCommand) => void
  disconnectDeadlines?: Partial<Record<SeatId, number>> | null
  onlineStatus?: WsStatus
  seatConnectionState?: MatchSeatConnectionState | null
  viewerSeatId?: SeatId
  hotSeat?: boolean
}

type SelectionState =
  | { kind: "none" }
  | { kind: "attacker"; instanceId: string }
  | { kind: "replace_support"; instanceId: string }
  | { kind: "jam_targeting"; sourceId: string; selection: SkillSelection }
  | { kind: "recycle_targeting"; sourceId: string; selection: SkillSelection }

type UnifiedDef = {
  name: string
  primaryColor: CardColor
  secondaryColor?: CardColor
  artwork?: CardArtwork
  skills: SkillSelection[]
  stats: { attack: number; health: number }
  cost?: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeCommandId(state: MatchState): string {
  return `cmd_t${state.turn.number}_a${state.turn.actionCount}`
}

function baseMeta(state: MatchState, seatId: SeatId) {
  return {
    commandId: makeCommandId(state),
    matchId: state.matchId,
    seatId,
    turnNumber: state.turn.number,
  }
}

function buildUnifiedDef(
  cardDef?: AuthoredCard,
  heroDef?: HeroDefinition,
  heroLevel?: 1 | 2 | 3,
): UnifiedDef | null {
  if (cardDef) {
    return {
      name: cardDef.name,
      primaryColor: cardDef.primaryColor,
      secondaryColor: cardDef.secondaryColor,
      artwork: cardDef.artwork,
      skills: cardDef.selectedSkills,
      stats: cardDef.stats,
      cost: cardDef.derivedCost,
    }
  }
  if (heroDef && heroLevel) {
    const prog = heroDef.progression[heroLevel - 1]
    return {
      name: heroDef.name,
      primaryColor: heroDef.primaryColor,
      secondaryColor: heroDef.secondaryColor,
      artwork: prog?.card.artwork,
      skills: prog?.card.selectedSkills ?? [],
      stats: prog?.card.stats ?? { attack: 0, health: 1 },
    }
  }
  return null
}

function getSystemCardDef(state: MatchState, definitionId: string) {
  switch (definitionId) {
    case "basic_battle_bot":
      return state.config.systemBattleBots.basicBattleBot
    case "spark_bot":
      return state.config.systemBattleBots.sparkBot
    case "better_battle_bot":
      return state.config.systemBattleBots.betterBattleBot
    default:
      return undefined
  }
}

function getBoardCardDef(
  state: MatchState,
  cardDefs: Map<string, AuthoredCard>,
  definitionId: string,
) {
  return cardDefs.get(definitionId) ?? getSystemCardDef(state, definitionId)
}

// ---------------------------------------------------------------------------
// MiniCard — horizontal 3-column card: art | stats+skills | actions
// ---------------------------------------------------------------------------

function MiniCard({
  inst,
  def,
  isHero,
  heroCurrentLevel,
  isAttacker,
  isSelected,
  isValidTarget,
  dimmed,
  hideContent,
  onBodyClick,
  actions,
}: {
  inst?: CardInstanceState
  def: UnifiedDef | null
  isHero?: boolean
  heroCurrentLevel?: 1 | 2 | 3
  isAttacker?: boolean
  isSelected?: boolean
  isValidTarget?: boolean
  dimmed?: boolean
  hideContent?: boolean
  onBodyClick?: () => void
  actions?: React.ReactNode
}) {
  const { t } = useI18n()
  const [artworkAssets] = useIdbState(
    ARTWORK_ASSET_STORAGE_KEY,
    ArtworkAssetStoreSchema,
    {
      defaultValue: EMPTY_ARTWORK_ASSET_STORE,
      version: ARTWORK_ASSET_STORAGE_VERSION,
    },
  )
  const artworkUrl = useMemo(
    () => resolveArtworkUrl(def?.artwork, artworkAssets, "thumb"),
    [def?.artwork, artworkAssets],
  )

  const hp = inst?.currentHealth ?? def?.stats.health ?? 1
  const maxHp = inst?.maxHealth ?? def?.stats.health ?? 1
  const atk = inst?.currentAttack ?? def?.stats.attack ?? 0
  const displayAtk =
    inst?.status.canAttackThisTurn &&
    inst.status.remainingAttack !== undefined &&
    inst.status.remainingAttack < atk
      ? inst.status.remainingAttack
      : atk
  const exhausted = inst?.status.exhausted ?? false
  const hasGuard =
    inst?.combatState.hasGuard ??
    (def?.skills ?? []).some((skill) => skill.skillId === "guard")
  const primaryColor = def?.primaryColor ?? "colorless"
  const factionColor = FACTION_PALETTE[primaryColor].base
  const miniCardVars = cardCssVars(primaryColor, def?.secondaryColor)

  const classes = [
    "mini-card",
    isAttacker ? "mini-card-attacker" : "",
    isSelected ? "mini-card-selected" : "",
    isValidTarget ? "mini-card-target" : "",
    exhausted ? "mini-card-exhausted" : "",
    dimmed ? "mini-card-dimmed" : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div
      className={classes}
      style={
        {
          ...(miniCardVars as React.CSSProperties),
          "--mini-faction": factionColor,
        } as React.CSSProperties
      }
    >
      {/* Art column */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: board card, pointer-only */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: role set conditionally */}
      <div
        className="mini-card-art"
        role={onBodyClick ? "button" : undefined}
        onClick={onBodyClick}
      >
        {hideContent ? (
          <div className="mini-card-facedown" />
        ) : artworkUrl ? (
          <img
            alt=""
            aria-hidden
            className="mini-card-art-img"
            src={artworkUrl}
          />
        ) : (
          <span className="mini-card-art-initial">
            {def?.name?.[0]?.toUpperCase() ?? "?"}
          </span>
        )}
      </div>

      {/* Body column */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: board card, pointer-only */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: role set conditionally */}
      <div
        className="mini-card-body"
        role={onBodyClick ? "button" : undefined}
        onClick={onBodyClick}
      >
        {hideContent ? (
          <span className="mini-card-name mini-card-hidden">—</span>
        ) : (
          <>
            <span className="mini-card-name">{def?.name ?? "?"}</span>
            <div className="mini-card-stats-row">
              <span className="mini-card-atk">⚔ {displayAtk}</span>
              <span className="mini-card-hp">
                ❤ {hp}/{maxHp}
              </span>
              {exhausted && (
                <span className="mini-card-badge mini-card-badge-exhausted">
                  zzz
                </span>
              )}
              {hasGuard && <span className="mini-card-badge">G</span>}
              {isHero && heroCurrentLevel && (
                <span className="mini-card-badge mini-card-badge-hero">
                  L{heroCurrentLevel}
                </span>
              )}
            </div>
            {(def?.skills ?? []).length > 0 && (
              <div className="mini-card-skills">
                {(def?.skills ?? []).slice(0, 4).map((s) => {
                  const Icon = SKILL_ICON[s.skillId as keyof typeof SKILL_ICON]
                  return Icon ? (
                    <span
                      className="mini-card-skill-chip"
                      key={`${s.skillId}:${s.mode ?? "default"}:${s.allyRequirement ?? 0}`}
                      title={getSkillTooltip(t, s)}
                    >
                      <span className="mini-card-skill-icon">
                        <Icon size="0.78em" strokeWidth={2} />
                      </span>
                      <span className="mini-card-skill-label">
                        {getSkillName(t, s.skillId)}
                      </span>
                      {(s.mode != null ||
                        s.allyRequirement != null ||
                        SKILL_MAP.get(s.skillId)?.isPassive === false) && (
                        <span className="mini-card-skill-modifiers">
                          {s.mode === "non_exhausting" && (
                            <span
                              className="mini-card-skill-modifier"
                              title={t("cardFace.skill.nonExhausting")}
                            >
                              <Star size={9} strokeWidth={2} />
                            </span>
                          )}
                          {s.mode === "self_destruct" && (
                            <span
                              className="mini-card-skill-modifier"
                              title={t("cardFace.skill.overload")}
                            >
                              <X size={9} strokeWidth={2} />
                            </span>
                          )}
                          {s.allyRequirement === 1 && (
                            <span
                              className="mini-card-skill-modifier"
                              title={t("cardFace.skill.ally.one")}
                            >
                              <AllyIcon size={9} strokeWidth={2} />
                            </span>
                          )}
                          {s.allyRequirement === 2 && (
                            <span
                              className="mini-card-skill-modifier"
                              title={t("cardFace.skill.ally.two")}
                            >
                              <DoubleAllyIcon size={9} strokeWidth={2} />
                            </span>
                          )}
                          {s.mode == null &&
                            SKILL_MAP.get(s.skillId)?.isPassive === false && (
                              <span
                                className="mini-card-skill-modifier"
                                title={t("cardFace.skill.exhaust")}
                              >
                                <ExhaustIcon size={9} strokeWidth={2} />
                              </span>
                            )}
                        </span>
                      )}
                    </span>
                  ) : null
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions column */}
      {actions && <div className="mini-card-actions">{actions}</div>}
    </div>
  )
}

function MiniActionButton({
  children,
  className = "",
  disabled,
  onClick,
  title,
}: {
  children: React.ReactNode
  className?: string
  disabled?: boolean
  onClick: () => void
  title: string
}) {
  return (
    <button
      aria-label={title}
      className={`mini-card-btn mini-card-btn-icon ${className}`.trim()}
      disabled={disabled}
      onClick={onClick}
      title={title}
      type="button"
    >
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// SeatPanel — one player's side of the board
// ---------------------------------------------------------------------------

function SeatPanel({
  seat,
  state,
  isActive,
  isFlipped,
  selection,
  cardDefs,
  heroDefs,
  seatConnectionState,
  hideHand,
  onTargetCard,
  onTargetCC,
  onPlayCard,
  onEnterReplaceMode,
  onStartAttack,
  onSwap,
  onAbilityClick,
  onCardExpand,
  onLevelUpHero,
  canLevelUpHero,
}: {
  seat: SeatState
  state: MatchState
  isActive: boolean
  isFlipped: boolean
  selection: SelectionState
  cardDefs: Map<string, AuthoredCard>
  heroDefs: Map<string, HeroDefinition>
  seatConnectionState?: MatchSeatConnectionState | null
  hideHand?: boolean
  onTargetCard: (instanceId: string) => void
  onTargetCC: (seatId: SeatId) => void
  onPlayCard: (instanceId: string, destination: "active" | "support") => void
  onEnterReplaceMode: (instanceId: string) => void
  onStartAttack: (instanceId: string) => void
  onSwap: (instanceId: string) => void
  onAbilityClick: (sourceId: string, skill: SkillSelection) => void
  onCardExpand: (instanceId: string) => void
  onLevelUpHero: () => void
  canLevelUpHero: boolean
}) {
  const { t } = useI18n()
  const inst = (id: string | null | undefined) =>
    id ? state.instances[id] : null

  const activeInst = inst(seat.zones.active)
  const activeId = seat.zones.active

  const isAttackerSelected = selection.kind === "attacker"
  const isJamTargeting = selection.kind === "jam_targeting"
  const isRecycleTargeting = selection.kind === "recycle_targeting"
  const isReplaceSupport = selection.kind === "replace_support"

  const opponentHasGuard = seatHasGuard(seat.seatId, state)
  const selectedAttacker =
    selection.kind === "attacker" ? state.instances[selection.instanceId] : null

  // Can this opponent unit be targeted (for attack or jam)?
  const canTargetEnemyUnit = (targetInst: CardInstanceState) => {
    if (isJamTargeting) return true
    if (!isAttackerSelected) return false
    if (selectedAttacker?.combatState.ignoresGuard) return true
    if (opponentHasGuard) return targetInst.combatState.hasGuard
    return true
  }

  const showCCTarget =
    isAttackerSelected &&
    seat.seatId !== state.activeSeat &&
    (!opponentHasGuard || selectedAttacker?.combatState.ignoresGuard)

  // Body-click behavior depends on context
  function cardBodyClick(
    instanceId: string,
    zone: "active" | "support" | "hand",
  ) {
    const isEnemy = seat.seatId !== state.activeSeat
    if (
      isRecycleTargeting &&
      (zone === "active" || zone === "support" || zone === "hand")
    ) {
      onTargetCard(instanceId)
      return
    }
    if (isReplaceSupport && zone === "support" && !isEnemy) {
      onTargetCard(instanceId)
      return
    }
    if (isJamTargeting && isEnemy) {
      onTargetCard(instanceId)
      return
    }
    if (isAttackerSelected && isEnemy) {
      const i = state.instances[instanceId]
      if (i && canTargetEnemyUnit(i)) {
        onTargetCard(instanceId)
        return
      }
    }
    onCardExpand(instanceId)
  }

  function getActivatableSkills(instanceId: string): SkillSelection[] {
    const i = state.instances[instanceId]
    if (!i) return []
    let skills: SkillSelection[]
    if (i.kind === "hero") {
      const ownerSeat = state.seats.find((s) => s.seatId === i.ownerSeatId)
      const level = ownerSeat?.hero.currentLevel ?? 1
      const heroDef = heroDefs.get(i.definitionId)
      skills = heroDef?.progression[level - 1]?.card.selectedSkills ?? []
    } else {
      skills =
        getBoardCardDef(state, cardDefs, i.definitionId)?.selectedSkills ?? []
    }
    return skills.filter((s) => ACTIVATABLE_SKILLS.has(s.skillId))
  }

  function abilityButtons(instanceId: string) {
    const skills = getActivatableSkills(instanceId)
    if (skills.length === 0) return null
    return (
      <>
        {skills.map((s) => {
          const Icon = SKILL_ICON[s.skillId as keyof typeof SKILL_ICON]
          return (
            <MiniActionButton
              key={`${instanceId}:${s.skillId}:${s.mode ?? "default"}:${s.allyRequirement ?? 0}`}
              onClick={() => onAbilityClick(instanceId, s)}
              title={getSkillTooltip(t, s)}
            >
              {Icon ? <Icon size={14} strokeWidth={2} /> : s.skillId[0]}
            </MiniActionButton>
          )
        })}
      </>
    )
  }

  function getDef(i: CardInstanceState): UnifiedDef | null {
    if (i.kind === "hero") {
      const heroDef = heroDefs.get(i.definitionId)
      const level = seat.hero.currentLevel as 1 | 2 | 3
      return buildUnifiedDef(undefined, heroDef, level)
    }
    return buildUnifiedDef(getBoardCardDef(state, cardDefs, i.definitionId))
  }

  const isHeroInst = (instanceId: string) =>
    instanceId === seat.hero.currentInstanceId

  const activeOccupied = seat.zones.active !== null
  const supportFull =
    seat.zones.support.length >= (state.config.supportCapacity ?? 3)
  const canSwap = !seat.oncePerTurn.hasUsedNormalSwap

  return (
    <div
      className={`seat-panel${isFlipped ? " seat-panel-flipped" : ""}${isActive ? " seat-panel-active" : ""}`}
    >
      {/* Header */}
      <div className="seat-header">
        <div className="seat-identity">
          <strong>{seat.displayName}</strong>
          {seatConnectionState?.[seat.seatId] === "disconnected" && (
            <span className="status-badge status-badge-warn">
              {t("match.board.playerDisconnected")}
            </span>
          )}
        </div>
        <div className="seat-resources">
          <span className="energy-badge">
            ⚡ {seat.resources.currentEnergy} / {seat.resources.maxEnergy}
          </span>
        </div>
      </div>

      {/* Central Command */}
      <div className="cc-row">
        <button
          className={`cc-chip${showCCTarget ? " cc-chip-target" : ""}`}
          disabled={!showCCTarget}
          onClick={showCCTarget ? () => onTargetCC(seat.seatId) : undefined}
          type="button"
        >
          <span className="cc-label">{t("match.board.cc")}</span>
          <span className="cc-health">
            {seat.centralCommand.currentHealth} /{" "}
            {seat.centralCommand.baseHealth +
              seat.centralCommand.handicapHealthBonus}
          </span>
        </button>
      </div>

      {/* Active zone */}
      <div className="zone-row zone-active">
        <span className="zone-label">{t("match.board.activeZone")}</span>
        {activeInst && activeId ? (
          <MiniCard
            inst={activeInst}
            def={getDef(activeInst)}
            isHero={isHeroInst(activeId)}
            heroCurrentLevel={
              isHeroInst(activeId)
                ? (seat.hero.currentLevel as 1 | 2 | 3)
                : undefined
            }
            isAttacker={
              selection.kind === "attacker" && selection.instanceId === activeId
            }
            isValidTarget={
              (isAttackerSelected &&
                seat.seatId !== state.activeSeat &&
                !!activeInst &&
                canTargetEnemyUnit(activeInst)) ||
              (isJamTargeting && seat.seatId !== state.activeSeat) ||
              (isRecycleTargeting && seat.seatId === state.activeSeat)
            }
            dimmed={!isActive}
            onBodyClick={() => cardBodyClick(activeId, "active")}
            actions={
              isActive ? (
                <>
                  {activeInst.status.canAttackThisTurn && (
                    <MiniActionButton
                      className="mini-card-btn-attack"
                      onClick={() => onStartAttack(activeId)}
                      title={`${t("match.board.attackerHint")} (${getDef(activeInst)?.name ?? "?"})`}
                    >
                      <AttackIcon size={14} strokeWidth={2.1} />
                    </MiniActionButton>
                  )}
                  {isHeroInst(activeId) && canLevelUpHero && (
                    <MiniActionButton
                      onClick={onLevelUpHero}
                      title={t("match.board.levelUpHero", {
                        cost: HERO_LEVEL_UP_COSTS[
                          (seat.hero.currentLevel + 1) as 2 | 3
                        ],
                      })}
                    >
                      <ChevronUp size={14} strokeWidth={2.2} />
                    </MiniActionButton>
                  )}
                  {abilityButtons(activeId)}
                </>
              ) : undefined
            }
          />
        ) : (
          <div className="zone-empty">—</div>
        )}
      </div>

      {/* Support zone */}
      <div className="zone-row zone-support">
        <span className="zone-label">{t("match.board.supportZone")}</span>
        <div className="support-slots">
          {seat.zones.support.map((id) => {
            const si = inst(id)
            if (!si) return null
            return (
              <MiniCard
                key={id}
                inst={si}
                def={getDef(si)}
                isHero={isHeroInst(id)}
                heroCurrentLevel={
                  isHeroInst(id)
                    ? (seat.hero.currentLevel as 1 | 2 | 3)
                    : undefined
                }
                isSelected={
                  isReplaceSupport && seat.seatId === state.activeSeat
                }
                isValidTarget={
                  (isAttackerSelected &&
                    seat.seatId !== state.activeSeat &&
                    canTargetEnemyUnit(si)) ||
                  (isJamTargeting && seat.seatId !== state.activeSeat) ||
                  (isRecycleTargeting && seat.seatId === state.activeSeat) ||
                  (isReplaceSupport && seat.seatId === state.activeSeat)
                }
                dimmed={!isActive}
                onBodyClick={() => cardBodyClick(id, "support")}
                actions={
                  isActive ? (
                    <>
                      {canSwap && (
                        <MiniActionButton
                          onClick={() => onSwap(id)}
                          title={`${t("match.board.activeDestination")} (${getDef(si)?.name ?? "?"})`}
                        >
                          <ArrowUpDown size={14} strokeWidth={2.1} />
                        </MiniActionButton>
                      )}
                      {isHeroInst(id) && canLevelUpHero && (
                        <MiniActionButton
                          onClick={onLevelUpHero}
                          title={t("match.board.levelUpHero", {
                            cost: HERO_LEVEL_UP_COSTS[
                              (seat.hero.currentLevel + 1) as 2 | 3
                            ],
                          })}
                        >
                          <ChevronUp size={14} strokeWidth={2.2} />
                        </MiniActionButton>
                      )}
                      {abilityButtons(id)}
                    </>
                  ) : undefined
                }
              />
            )
          })}
          {seat.zones.support.length === 0 && (
            <div className="zone-empty">—</div>
          )}
        </div>
      </div>

      {/* Hand */}
      <div className="zone-row zone-hand">
        <span className="zone-label">
          {t("match.board.hand", { count: seat.zones.hand.length })}
        </span>
        <div className="hand-cards">
          {seat.zones.hand.map((id) => {
            const hi = inst(id)
            if (!hi) return null
            return (
              <MiniCard
                key={id}
                inst={hi}
                def={hideHand ? null : getDef(hi)}
                isHero={isHeroInst(id)}
                heroCurrentLevel={
                  isHeroInst(id)
                    ? (seat.hero.currentLevel as 1 | 2 | 3)
                    : undefined
                }
                isValidTarget={
                  isRecycleTargeting && seat.seatId === state.activeSeat
                }
                dimmed={!isActive}
                hideContent={hideHand}
                onBodyClick={
                  hideHand ? undefined : () => cardBodyClick(id, "hand")
                }
                actions={
                  isActive && !hideHand ? (
                    <>
                      <MiniActionButton
                        disabled={activeOccupied}
                        onClick={() => onPlayCard(id, "active")}
                        title={`${t("match.board.activeDestination")} (${getDef(hi)?.name ?? "?"})`}
                      >
                        <ArrowUpToLine size={14} strokeWidth={2.1} />
                      </MiniActionButton>
                      <MiniActionButton
                        onClick={() =>
                          supportFull
                            ? onEnterReplaceMode(id)
                            : onPlayCard(id, "support")
                        }
                        title={`${t("match.board.supportDestination")} (${getDef(hi)?.name ?? "?"})`}
                      >
                        <ArrowDownToLine size={14} strokeWidth={2.1} />
                      </MiniActionButton>
                    </>
                  ) : undefined
                }
              />
            )
          })}
          {seat.zones.hand.length === 0 && (
            <div className="zone-empty">{t("match.board.empty")}</div>
          )}
        </div>
      </div>

      {/* Deck/discard counts */}
      <div className="zone-counts">
        <span>
          {t("match.board.deck", { count: seat.zones.drawDeck.length })}
        </span>
        <span>
          {t("match.board.discard", { count: seat.zones.discard.length })}
        </span>
        <span>
          {t("match.board.supply", { count: seat.zones.supply.length })}
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main board
// ---------------------------------------------------------------------------

export function MatchBoard({
  initialState,
  onReset,
  cardDefs,
  heroDefs,
  controlledState,
  onSendCommand,
  disconnectDeadlines,
  onlineStatus,
  seatConnectionState,
  viewerSeatId,
  hotSeat,
}: Props) {
  const { t } = useI18n()
  const [state, setState] = useState<MatchState>(initialState)
  const [selection, setSelection] = useState<SelectionState>({ kind: "none" })
  const [lastError, setLastError] = useState<string | null>(null)
  const [clockNow, setClockNow] = useState(() => Date.now())
  const [expandedInstanceId, setExpandedInstanceId] = useState<string | null>(
    null,
  )
  const [expandedPreviewDef, setExpandedPreviewDef] =
    useState<UnifiedDef | null>(null)
  const [hotSeatSplash, setHotSeatSplash] = useState<SeatId | null>(
    hotSeat ? initialState.activeSeat : null,
  )
  const isOnlineBoard = controlledState !== undefined
  const commandsBlockedByReconnect = isOnlineBoard && onlineStatus !== "open"

  useEffect(() => {
    if (controlledState) setState(controlledState)
  }, [controlledState])

  const activeSeatId = state.activeSeat
  const activeSeat = getSeat(state, activeSeatId)
  const opponentSeatId: SeatId = activeSeatId === "seat_a" ? "seat_b" : "seat_a"
  const opponentSeat = getSeat(state, opponentSeatId)
  const viewerOpponentSeatId: SeatId | null = viewerSeatId
    ? viewerSeatId === "seat_a"
      ? "seat_b"
      : "seat_a"
    : null
  const opponentDisconnected =
    viewerOpponentSeatId !== null &&
    seatConnectionState?.[viewerOpponentSeatId] === "disconnected"
  const opponentDisconnectDeadline =
    viewerOpponentSeatId !== null
      ? disconnectDeadlines?.[viewerOpponentSeatId]
      : undefined
  const opponentDisconnectSeconds =
    typeof opponentDisconnectDeadline === "number"
      ? Math.max(0, Math.ceil((opponentDisconnectDeadline - clockNow) / 1000))
      : null

  useEffect(() => {
    if (!opponentDisconnected || opponentDisconnectSeconds === null) return
    const timer = window.setInterval(() => {
      setClockNow(Date.now())
    }, 1_000)
    return () => window.clearInterval(timer)
  }, [opponentDisconnected, opponentDisconnectSeconds])

  function dispatch(cmd: ActionCommand): MatchState | null {
    if (commandsBlockedByReconnect) {
      setLastError(t("match.board.reconnectingBlocked"))
      return null
    }
    const result = executeCommand(state, cmd, { heroes: heroDefs })
    if (result.ok) {
      setState(result.state)
      setLastError(null)
      setSelection({ kind: "none" })
      onSendCommand?.(cmd)
      return result.state
    }
    setLastError(getMatchCommandErrorLabel(t, result))
    return null
  }

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  function handleTargetCard(instanceId: string) {
    const meta = baseMeta(state, activeSeatId)
    const seat = activeSeat

    if (selection.kind === "recycle_targeting") {
      dispatch({
        ...meta,
        kind: "use_ability",
        payload: {
          sourceId: selection.sourceId,
          skillId: selection.selection.skillId,
          mode: selection.selection.mode,
          allyRequirement: selection.selection.allyRequirement,
          target: { instanceId },
        },
      })
      return
    }

    if (selection.kind === "jam_targeting") {
      const isMine =
        seat.zones.active === instanceId ||
        seat.zones.support.includes(instanceId)
      if (!isMine) {
        dispatch({
          ...meta,
          kind: "use_ability",
          payload: {
            sourceId: selection.sourceId,
            skillId: selection.selection.skillId,
            mode: selection.selection.mode,
            allyRequirement: selection.selection.allyRequirement,
            target: { instanceId },
          },
        })
      }
      return
    }

    if (selection.kind === "attacker") {
      const isMine =
        seat.zones.active === instanceId ||
        seat.zones.support.includes(instanceId)
      if (!isMine) {
        dispatch({
          ...meta,
          kind: "attack",
          payload: {
            attackerId: selection.instanceId,
            target: { type: "unit", seatId: opponentSeatId, instanceId },
          },
        })
      }
      return
    }

    if (selection.kind === "replace_support") {
      if (seat.zones.support.includes(instanceId)) {
        dispatch({
          ...meta,
          kind: "play_card",
          payload: {
            instanceId: selection.instanceId,
            destination: "support",
            replaceSupportId: instanceId,
          },
        })
      }
      return
    }
  }

  function handleTargetCC(seatId: SeatId) {
    if (selection.kind !== "attacker") return
    dispatch({
      ...baseMeta(state, activeSeatId),
      kind: "attack",
      payload: {
        attackerId: selection.instanceId,
        target: { type: "central_command", seatId },
      },
    })
  }

  function handlePlayCard(
    instanceId: string,
    destination: "active" | "support",
  ) {
    dispatch({
      ...baseMeta(state, activeSeatId),
      kind: "play_card",
      payload: { instanceId, destination },
    })
  }

  function handleEnterReplaceMode(instanceId: string) {
    setLastError(null)
    setSelection({ kind: "replace_support", instanceId })
  }

  function handleStartAttack(instanceId: string) {
    setLastError(null)
    if (selection.kind === "attacker" && selection.instanceId === instanceId) {
      setSelection({ kind: "none" })
    } else {
      setSelection({ kind: "attacker", instanceId })
    }
  }

  function handleSwap(instanceId: string) {
    dispatch({
      ...baseMeta(state, activeSeatId),
      kind: "swap_active_unit",
      payload: { fromSupportId: instanceId },
    })
  }

  function handleRecruit(
    source: "merc_row" | "better_battle_bot_lane",
    slotId?: string,
  ) {
    dispatch({
      ...baseMeta(state, activeSeatId),
      kind: "recruit_card",
      payload: { source, slotId },
    })
  }

  function handleLevelUpHero() {
    const targetLevel = (activeSeat.hero.currentLevel + 1) as 2 | 3
    dispatch({
      ...baseMeta(state, activeSeatId),
      kind: "level_up_hero",
      payload: { targetLevel },
    })
  }

  function handleEndTurn() {
    const newState = dispatch({
      ...baseMeta(state, activeSeatId),
      kind: "end_turn",
      payload: {},
    })
    if (hotSeat && newState && newState.status === "active") {
      setHotSeatSplash(newState.activeSeat)
    }
  }

  function handleConcede() {
    dispatch({
      ...baseMeta(state, activeSeatId),
      kind: "concede",
      payload: {},
    })
  }

  function handleUseAbility(sourceId: string, skill: SkillSelection) {
    if (skill.skillId === "jam") {
      setLastError(null)
      setSelection({ kind: "jam_targeting", sourceId, selection: skill })
      return
    }
    if (skill.skillId === "recycle") {
      setLastError(null)
      setSelection({ kind: "recycle_targeting", sourceId, selection: skill })
      return
    }
    dispatch({
      ...baseMeta(state, activeSeatId),
      kind: "use_ability",
      payload: {
        sourceId,
        skillId: skill.skillId,
        mode: skill.mode,
        allyRequirement: skill.allyRequirement,
      },
    })
  }

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const isCompleted = state.status === "completed"
  const betterBattleBotDef = buildUnifiedDef(
    state.config.systemBattleBots.betterBattleBot,
  )
  const bbbCost = betterBattleBotDef?.cost ?? 2

  const canLevelUpHero =
    activeSeat.hero.currentLevel < 3 &&
    (() => {
      const lvlCost =
        HERO_LEVEL_UP_COSTS[(activeSeat.hero.currentLevel + 1) as 2 | 3]
      const heroInst = state.instances[activeSeat.hero.currentInstanceId]
      return (
        activeSeat.resources.currentEnergy >= lvlCost &&
        !!heroInst &&
        !heroInst.status.exhausted
      )
    })()

  const resultDetail =
    state.result?.reason === "timeout" && viewerSeatId
      ? state.result.winnerSeat === viewerSeatId
        ? t("match.board.timeout.youWin")
        : state.result.loserSeat === viewerSeatId
          ? t("match.board.timeout.youLose")
          : null
      : null

  // Expanded card for full-view modal
  const expandedInst = expandedInstanceId
    ? state.instances[expandedInstanceId]
    : null
  const expandedCardDef =
    expandedInst?.kind !== "hero"
      ? getBoardCardDef(state, cardDefs, expandedInst?.definitionId ?? "")
      : undefined
  const expandedHeroDef =
    expandedInst?.kind === "hero"
      ? heroDefs.get(expandedInst.definitionId)
      : undefined
  const expandedHeroLevel =
    expandedInst?.kind === "hero"
      ? (() => {
          const ownerSeat = state.seats.find(
            (s) => s.seatId === expandedInst.ownerSeatId,
          )
          return (ownerSeat?.hero.currentLevel ?? 1) as 1 | 2 | 3
        })()
      : undefined
  const expandedDef =
    expandedPreviewDef ??
    (expandedCardDef || expandedHeroDef
      ? buildUnifiedDef(expandedCardDef, expandedHeroDef, expandedHeroLevel)
      : null)

  function openExpandedInstance(instanceId: string) {
    setExpandedPreviewDef(null)
    setExpandedInstanceId(instanceId)
  }

  function closeExpandedCard() {
    setExpandedInstanceId(null)
    setExpandedPreviewDef(null)
  }

  useEffect(() => {
    if (!expandedInst && !expandedDef) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        setExpandedInstanceId(null)
        setExpandedPreviewDef(null)
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [expandedDef, expandedInst])

  // Shared props for both SeatPanels
  const sharedSeatPanelProps = {
    cardDefs,
    heroDefs,
    state,
    selection,
    seatConnectionState,
    onTargetCard: handleTargetCard,
    onTargetCC: handleTargetCC,
    onPlayCard: handlePlayCard,
    onEnterReplaceMode: handleEnterReplaceMode,
    onStartAttack: handleStartAttack,
    onSwap: handleSwap,
    onAbilityClick: handleUseAbility,
    onCardExpand: openExpandedInstance,
    onLevelUpHero: handleLevelUpHero,
    canLevelUpHero,
  }

  function MercRow({ seat, label }: { seat: SeatState; label: string }) {
    return (
      <div className="merc-row-section">
        <span className="zone-label">{label}</span>
        <div className="merc-row-slots">
          {seat.zones.mercRow.map((slotId) => {
            const mi = state.instances[slotId]
            if (!mi) return null
            const miDef = buildUnifiedDef(
              getBoardCardDef(state, cardDefs, mi.definitionId),
            )
            const cost =
              miDef?.cost ??
              mi.recruitCost ??
              mi.currentAttack + mi.currentHealth
            const canAfford = activeSeat.resources.currentEnergy >= cost
            return (
              <MiniCard
                key={slotId}
                inst={mi}
                def={miDef}
                actions={
                  <button
                    className={`mini-card-btn${canAfford ? " mini-card-btn-affordable" : ""}`}
                    onClick={() => handleRecruit("merc_row", slotId)}
                    title={`${miDef?.name ?? "?"} • ${cost}⚡`}
                    type="button"
                  >
                    {cost}
                    <EnergyIcon size={12} strokeWidth={2.1} />
                  </button>
                }
                onBodyClick={() => openExpandedInstance(slotId)}
              />
            )
          })}
          {seat.zones.mercRow.length === 0 && (
            <span className="zone-empty">
              {t("match.board.supplyExhausted")}
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="match-board">
      {/* Match result overlay */}
      {isCompleted && state.result && (
        <div className="match-result-overlay">
          <div className="match-result-card">
            <h2>{t("match.board.matchOver")}</h2>
            <p className="match-result-winner">
              {t("match.board.winner", {
                name: getSeat(state, state.result.winnerSeat).displayName,
              })}
            </p>
            <p className="match-result-reason">
              {t("match.board.reason.label", {
                reason: t(
                  `match.board.reason.${
                    state.result.reason === "central_command_zero"
                      ? "centralCommandZero"
                      : state.result.reason
                  }` as const,
                ),
              })}
            </p>
            {resultDetail && (
              <p className="match-result-detail">{resultDetail}</p>
            )}
            <p className="match-result-turns">
              {t("match.board.turns", { count: state.turn.number })}
            </p>
            <button className="primary-button" onClick={onReset} type="button">
              {t("match.board.newMatch")}
            </button>
          </div>
        </div>
      )}

      {/* Hot seat splash */}
      {hotSeatSplash && (
        <div className="hot-seat-splash">
          <div className="hot-seat-splash-card">
            <p className="hot-seat-splash-eyebrow">
              {t("match.board.turn", { turn: state.turn.number })}
            </p>
            <h2 className="hot-seat-splash-title">
              {t("match.hotSeat.readyTitle", {
                name: getSeat(state, hotSeatSplash).displayName,
              })}
            </h2>
            <button
              className="primary-button"
              onClick={() => setHotSeatSplash(null)}
              type="button"
            >
              {t("match.hotSeat.playTurn")}
            </button>
          </div>
        </div>
      )}

      {/* Card expand modal */}
      {(expandedInst || expandedDef) && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss, pointer-only
        // biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss
        <div className="card-expand-backdrop" onClick={closeExpandedCard}>
          {expandedDef && (
            // biome-ignore lint/a11y/noStaticElementInteractions: stop propagation
            // biome-ignore lint/a11y/useKeyWithClickEvents: stop propagation
            <div
              className="card-expand-face"
              onClick={(e) => e.stopPropagation()}
            >
              <CardFace
                name={expandedDef.name}
                primaryColor={expandedDef.primaryColor}
                secondaryColor={expandedDef.secondaryColor}
                artwork={expandedDef.artwork}
                cost={expandedDef.cost}
                stats={expandedDef.stats}
                skills={expandedDef.skills}
                isHero={!!expandedHeroDef}
                heroLevel={expandedHeroLevel}
                currentHp={expandedInst?.currentHealth}
                exhausted={expandedInst?.status.exhausted}
                showGuardBadge={expandedInst?.combatState.hasGuard}
                size="lg"
                style={{ width: "min(20rem, 80vw)" }}
              />
            </div>
          )}
        </div>
      )}

      {/* Board header */}
      <div className="board-header">
        <span className="board-turn">
          {t("match.board.turn", { turn: state.turn.number })}
        </span>
        <span className="board-active">
          {t("match.board.activeLabel", { name: activeSeat.displayName })}
        </span>
        <span className="board-step">
          {t(`match.board.turnStep.${state.turn.step}` as const)}
        </span>
        <button
          className="ghost-icon-button danger-text"
          onClick={onReset}
          type="button"
        >
          {t("match.board.quit")}
        </button>
      </div>

      {isOnlineBoard && onlineStatus && onlineStatus !== "open" && (
        <output className="match-connection-banner">
          {onlineStatus === "connecting"
            ? t("match.board.reconnecting")
            : t("match.board.connectionUnstable")}
        </output>
      )}

      {isOnlineBoard && opponentDisconnected && (
        <output className="match-connection-banner match-connection-banner-warn">
          {t("match.board.opponentDisconnected", {
            seconds: opponentDisconnectSeconds ?? 0,
          })}
        </output>
      )}

      {/* Opponent seat (flipped) */}
      <SeatPanel
        {...sharedSeatPanelProps}
        hideHand={hotSeat}
        isActive={false}
        isFlipped
        seat={opponentSeat}
      />

      {/* Merc rows */}
      <div className="shared-zones">
        <MercRow
          seat={opponentSeat}
          label={`${opponentSeat.displayName}: ${t("match.board.mercRow", { count: opponentSeat.zones.mercRow.length })}`}
        />
        <MercRow
          seat={activeSeat}
          label={`${activeSeat.displayName}: ${t("match.board.mercRow", { count: activeSeat.zones.mercRow.length })}`}
        />

        {/* Better Battle Bot lane */}
        <div className="bbb-section">
          <MiniCard
            def={betterBattleBotDef}
            onBodyClick={() => {
              setExpandedInstanceId(null)
              setExpandedPreviewDef(betterBattleBotDef)
            }}
            actions={
              <button
                className={`mini-card-btn${activeSeat.resources.currentEnergy >= bbbCost ? " mini-card-btn-affordable" : ""}`}
                onClick={() => handleRecruit("better_battle_bot_lane")}
                title={`${betterBattleBotDef?.name ?? t("match.board.betterBattleBot")}\n${bbbCost}⚡`}
                type="button"
              >
                {bbbCost}
                <EnergyIcon size={12} strokeWidth={2.1} />
              </button>
            }
          />
        </div>
      </div>

      {/* Active seat */}
      <SeatPanel
        {...sharedSeatPanelProps}
        isActive
        isFlipped={false}
        seat={activeSeat}
      />

      {/* Action panel */}
      <div className="action-panel">
        {selection.kind === "attacker" && (
          <div className="action-group">
            <span className="action-hint">{t("match.board.attackerHint")}</span>
            <button
              className="ghost-icon-button"
              onClick={() => setSelection({ kind: "none" })}
              type="button"
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
        {selection.kind === "replace_support" && (
          <div className="action-group">
            <span className="action-hint">
              {t("match.board.supportDestination")} — pick a card to replace
            </span>
            <button
              className="ghost-icon-button"
              onClick={() => setSelection({ kind: "none" })}
              type="button"
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
        {selection.kind === "jam_targeting" && (
          <div className="action-group">
            <span className="action-hint">{t("match.board.jamHint")}</span>
            <button
              className="ghost-icon-button"
              onClick={() => setSelection({ kind: "none" })}
              type="button"
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
        {selection.kind === "recycle_targeting" && (
          <div className="action-group">
            <span className="action-hint">{t("match.board.recycleHint")}</span>
            <button
              className="ghost-icon-button"
              onClick={() => setSelection({ kind: "none" })}
              type="button"
            >
              {t("common.cancel")}
            </button>
          </div>
        )}

        <button
          className="ghost-icon-button danger-text"
          onClick={handleConcede}
          type="button"
        >
          {t("match.board.concede")}
        </button>

        <button
          className="primary-button action-end-turn"
          onClick={handleEndTurn}
          type="button"
        >
          {t("match.board.endTurn")}
        </button>
      </div>

      {lastError && <div className="board-error">{lastError}</div>}
    </div>
  )
}
